import { Component, inject, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { AccountService } from '../../service/account.service';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { EditUserVrchatComponent } from '../admin/users/dialog/edit-user/edit-user-vrchat/edit-user-vrchat.component';
import { EditUserPatreonComponent } from '../admin/users/dialog/edit-user/edit-user-patreon/edit-user-patreon.component';
import { ConnectVRChatComponent } from '../../dialog/connect-vrchat/connect-vrchat.component';
import { ConnectPatreonComponent } from '../../dialog/connect-patreon/connect-patreon.component';
import { DiscordAccountInfoComponent } from '../../dialog/discord-account-info/discord-account-info.component';
import { PatreonIconComponent } from "../../helper/patreon-icon/patreon-icon.component";
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ApiService } from '../../service/api.service';
import { MatSelect, MatFormField, MatOption, MatLabel, MatSelectTrigger } from "@angular/material/select";

@Component({
  selector: 'app-account-settings',
  imports: [CommonModule, MatListModule, MatIconModule, MatCardModule, PatreonIconComponent, MatButtonModule, MatSnackBarModule, MatSelect, MatFormField, MatOption, MatLabel, MatSelectTrigger],
  templateUrl: './account-settings.component.html',
  styleUrl: './account-settings.component.less'
})
export class AccountSettingsComponent implements OnInit {

  @Inject(AccountService) accountService = inject(AccountService);
  @Inject(ApiService) apiService = inject(ApiService);
  dialog = inject(MatDialog);
  router = inject(Router);
  snackBar = inject(MatSnackBar);

  selectedPreferences: number[] = [];
  _settingAASubmitAs?: string;

  get settingAASubmitAs(): string {
    if (this._settingAASubmitAs == "auto") {
      if (this.accountService.account.value?.connections.vrchat?.username) {
        return "vrchat";
      }
      return "discord";
    }
    return this._settingAASubmitAs ?? "discord";
  }

  set settingAASubmitAs(value: string) {
    this._settingAASubmitAs = value;
    this.apiService.post("account/saveSettingAASubmitAs.php", {
      settingAASubmitAs: value
    }).subscribe({
      next: (data: any) => {
        if (data.code === 200) {
          // Reload account to get updated preferences
          this.accountService.loadAccount();
          // Show success message
          this.snackBar.open('Setting saved successfully', 'Close', {
            duration: 3000,
            panelClass: ['success-snackbar']
          });
        } else {
          // Show error message for non-200 response
          const errorMessage = data.message || 'Failed to save setting';
          this.snackBar.open(errorMessage, 'Close', {
            duration: 5000,
            panelClass: ['error-snackbar']
          });
        }
      },
      error: (error: any) => {
        // Handle network errors or other exceptions
        console.error('Error saving setting:', error);
        const errorMessage = error?.error?.message || error?.message || 'Failed to save setting. Please try again.';
        this.snackBar.open(errorMessage, 'Close', {
          duration: 5000,
          panelClass: ['error-snackbar']
        });
      }
    });
  }

  ngOnInit() {
    this.accountService.account.subscribe((account) => {
      // undefined means account is still loading, wait for it
      if (account === undefined) {
        return;
      }
      
      this._settingAASubmitAs = account?.settings?.aaSubmitAs ?? "auto";

      // null means user is not logged in, redirect to login
      if (account === null) {
        console.log("Not logged in - redirecting to login");
        this.router.navigate(["/login"]);
        return;
      }
      
      // account is loaded, load notification preferences
      const preferences = account.notification_preferences ?? 0;
      this.selectedPreferences = [];
      if ((preferences & 1) === 1) this.selectedPreferences.push(1);
      if ((preferences & 2) === 2) this.selectedPreferences.push(2);
      if ((preferences & 4) === 4) this.selectedPreferences.push(4);
    });
  }

  isSelected(id: number): boolean {
    return this.selectedPreferences.includes(id);
  }

  onSelectionChange(event: any) {
    // Update selectedPreferences based on mat-selection-list selection
    this.selectedPreferences = event.source.selectedOptions.selected.map((option: any) => option.value);
  }

  saveNotificationPreferences() {
    // Calculate bitflag from selected items
    let preferences = 0;
    if (this.selectedPreferences.includes(1)) preferences |= 1;
    if (this.selectedPreferences.includes(2)) preferences |= 2;
    if (this.selectedPreferences.includes(4)) preferences |= 4;

    this.apiService.post("account/saveNotificationPreferences.php", {
      preferences: preferences
    }).subscribe({
      next: (data: any) => {
        if (data.code === 200) {
          // Reload account to get updated preferences
          this.accountService.loadAccount();
          // Show success message
          this.snackBar.open('Notification preferences saved successfully', 'Close', {
            duration: 3000,
            panelClass: ['success-snackbar']
          });
        } else {
          // Show error message for non-200 response
          const errorMessage = data.message || 'Failed to save notification preferences';
          this.snackBar.open(errorMessage, 'Close', {
            duration: 5000,
            panelClass: ['error-snackbar']
          });
        }
      },
      error: (error: any) => {
        // Handle network errors or other exceptions
        console.error('Error saving notification preferences:', error);
        const errorMessage = error?.error?.message || error?.message || 'Failed to save notification preferences. Please try again.';
        this.snackBar.open(errorMessage, 'Close', {
          duration: 5000,
          panelClass: ['error-snackbar']
        });
      }
    });
  }

  openVRChatAccount() {
    this.dialog.open(ConnectVRChatComponent, {
      data: {
        user: this.accountService.account.value
      }
    });
  }

  openPatreonAccount() {
    this.dialog.open(ConnectPatreonComponent, {
      data: {
        user: this.accountService.account.value
      }
    });
  }

  openDiscordAccount() {
    this.dialog.open(DiscordAccountInfoComponent, {
      data: {
        user: this.accountService.account.value
      }
    });
  }

  notificationCategories: {
    name: string, 
    icon: string,
    categories: {
      name: string,
      id: number,
      description: string,
      enabled: boolean,
      icon: string
    }[]}[] =  [
      // No notification categories for now
  ]
}
